import type { MySsApiResponse } from '~/types/MySsApiResponse';
import type { MySsException } from '~/types/MySsException';

import authService from '~/services/authService';
import axios from 'axios';
import { ExceptionHandler } from '~/utils/ExceptionHandler';
import { useAppStore } from '~/stores/useAppStore';
import { useAuthStore } from '~/stores/useAuthStore';

export function useHttp() {
  const { session, logout } = useAuthStore();

  const http = axios.create({
    baseURL: useAppStore().baseURL,
    timeout: 60000,
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      'Access-Control-Allow-Origin': '*',
      withCredentials: true,
      authorization: `Bearer ${session.token}`,
      'refresh-token': `Bearer ${session.refreshToken}`,
      'myscansource-manager-api': useAppStore().apiKey,
    },
  });

  http.interceptors.request.use(
    async function (config) {
      if (config.headers.get('mayBeUnauthenticated') === undefined) {
        await authService.CallTokenRefresh();
      }
      return config;
    },
    function (error) {
      return Promise.reject(error);
    },
  );

  http.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      if (
        error?.response?.status === 500 ||
        error.code === 'ERR_NETWORK' ||
        error?.response?.status === 403
      ) {
        if (error.code === 'ERR_NETWORK' || error?.response?.status === 403) {
          logout(false, true);
        } else {
          logout(true);
        }
      }
      return Promise.reject(error);
    },
  );
  return { http };
}
export async function get(
  url: string,
  params?: object,
  config?: object,
): Promise<MySsApiResponse> {
  return useHttp()
    .http.get(url, { params, ...config })
    .then(
      (response) => response,
      (error) => {
        return ExceptionHandler(error);
      },
    );
}
export async function post(
  url: string,
  data: object,
  config?: object,
): Promise<MySsApiResponse['data'] | MySsException> {
  return useHttp()
    .http.post(url, data, config)
    .then(
      (response) => response.data,
      (error) => {
        throw ExceptionHandler(error);
      },
    );
}
export async function put(
  url: string,
  data: object,
  config?: object,
): Promise<MySsApiResponse['data'] | MySsException> {
  return useHttp()
    .http.put(url, data, config)
    .then(
      (response) => response.data,
      (error) => {
        throw ExceptionHandler(error);
      },
    );
}
export async function patch(
  url: string,
  data: object,
  config?: object,
): Promise<MySsApiResponse['data'] | MySsException> {
  return useHttp()
    .http.patch(url, data, config)
    .then(
      (response) => response.data,
      (error) => {
        throw ExceptionHandler(error);
      },
    );
}
